/**
 * This file is a migration of the Authentication code scaffolded using .NET Core Web Application for SPA (React)
 * with Individual Authentication.
 *
 * See here: https://docs.microsoft.com/en-us/dotnet/core/tools/dotnet-new#spa
 */
import { Profile, User, UserManager, WebStorageStateStore } from 'oidc-client';
import config from 'config';
import LogApi from 'api/LogApi';

export interface AuthResult {
  status: AuthenticationResultStatus;
  state?: State;
  message?: any;
}

interface State {
  returnUrl: string;
}

export type AuthenticationResultStatus = 'redirect' | 'success' | 'fail';

const _callbacks: Array<{ callback: () => any; subscription: number }> = [];
let _nextSubscriptionId: number = 0;
let _user: User = null;
let _isAuthenticated: boolean = false;
let userManager: UserManager;

async function ensureUserManagerInitialized(): Promise<void> {
  if (userManager !== undefined) {
    return;
  }

  const response = await fetch(config.authOidcMetadata);
  if (!response.ok) {
    throw new Error(`Could not load settings for '${config.applicationName}'`);
  }

  const authOidcMetadata = await response.json();
  const settings = {
    authority: config.authBaseUrl,
    client_id: config.applicationName,
    redirect_uri: `${window.location.origin}/authentication/login-callback`,
    post_logout_redirect_uri: `${window.location.origin}/authentication/logout-callback`,
    response_type: 'code',
    scope: 'openid profile biod.insights.api biod.account.api offline_access',
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: true,
    userStore: new WebStorageStateStore({
      prefix: config.applicationName,
    }),
    metadata: {
      ...authOidcMetadata,
    },
  };

  userManager = new UserManager(settings);

  userManager.events.addUserSignedOut(async () => {
    await userManager.removeUser();
    updateState(undefined);
  });

  await userManager.clearStaleState();
}

function updateState(user): void {
  _user = user;
  _isAuthenticated = !!_user;
  notifySubscribers();
}

function notifySubscribers(): void {
  _callbacks.forEach(c => c.callback());
}

function createArguments(state?: State): { useReplaceToNavigate: boolean; data: State } {
  return { useReplaceToNavigate: true, data: state };
}

function error(message): AuthResult {
  return { status: 'fail', message };
}

function success(state: State): AuthResult {
  return { status: 'success', state };
}

function redirect(): AuthResult {
  return { status: 'redirect' };
}

export async function isAuthenticated(): Promise<boolean> {
  const user = await getUser();
  return !!user;
}

export async function getUser(): Promise<Profile> {
  if (_user && _user.profile) {
    return _user.profile;
  }

  await ensureUserManagerInitialized();
  const user = await userManager.getUser();
  return user && user.profile;
}

export async function getAccessToken(): Promise<string> {
  await ensureUserManagerInitialized();
  const user = await userManager.getUser();
  return user && user.access_token;
}

export async function signInRedirect(state): Promise<AuthResult> {
  await userManager.signinRedirect(createArguments(state));
  return redirect();
}

export async function signIn(state): Promise<AuthResult> {
  await ensureUserManagerInitialized();
  try {
    const silentUser = await userManager.signinSilent(createArguments());
    updateState(silentUser);
    return success(state);
  } catch (silentError) {
    try {
      await userManager.signinRedirect(createArguments(state));
      return redirect();
    } catch (redirectError) {
      LogApi.sendLog('error', `Redirect authentication error: ${redirectError}`);
      console.log('Redirect authentication error: ', redirectError);
      return error(redirectError);
    }
  }
}

export async function signInSilent(): Promise<void> {
  await ensureUserManagerInitialized();
  try {
    const silentUser = await userManager.signinSilent(createArguments());
    updateState(silentUser);
  } catch (silentError) {
    // not-empty
  }
}

export async function completeSignIn(url): Promise<AuthResult> {
  if (await isAuthenticated()) {
    // User is already logged in, the url is likely old
    const existingUser = await getUser();
    return success(existingUser.state);
  }

  try {
    await ensureUserManagerInitialized();
    const user = await userManager.signinCallback(url);
    updateState(user);
    return success(user && user.state);
  } catch (signInError) {
    LogApi.sendLog('error', `There was an error signing in: ${signInError}`);
    console.log('There was an error signing in: ', signInError);
    return error('There was an error signing in.');
  }
}

export async function signOut(state?: { returnUrl: string }): Promise<AuthResult> {
  try {
    await userManager.signoutRedirect(createArguments(state));
    return redirect();
  } catch (redirectSignOutError) {
    LogApi.sendLog('error', `Redirect signout error: ${redirectSignOutError}`);
    console.log('Redirect signout error: ', redirectSignOutError);
    return error(redirectSignOutError);
  }
}

export async function completeSignOut(url): Promise<AuthResult> {
  await ensureUserManagerInitialized();
  try {
    const response = await userManager.signoutCallback(url);
    updateState(null);
    return success(response && (response as any).data);
  } catch (signOutError) {
    LogApi.sendLog('error', `There was an error trying to log out ${signOutError}`);
    console.log(`There was an error trying to log out '${signOutError}'.`);
    return error(signOutError);
  }
}

export function subscribe(callback): number {
  _callbacks.push({ callback, subscription: _nextSubscriptionId++ });
  return _nextSubscriptionId - 1;
}

export function unsubscribe(subscriptionId): void {
  const subscriptionIndex = _callbacks
    .map((element, index) =>
      element.subscription === subscriptionId ? { found: true, index } : { found: false }
    )
    .filter(element => element.found === true);
  if (subscriptionIndex.length !== 1) {
    throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
  }

  _callbacks.splice(subscriptionIndex[0].index, 1);
}
